import { Chip, ChipProps } from '@mui/material';
import { useTheme } from "@mui/material/styles";

const STATIC_STATUS_STYLES: Record<string, { bgColor: string; color: string }> = {
	SUCCESS: { bgColor: '#01C008', color: '#fff' },
	SUCCESSFUL: { bgColor: '#01C008', color: '#fff' },
	YES: { bgColor: '#01C008', color: '#fff' },
	COMPLETED: { bgColor: '#01C008', color: '#fff' },
	ONLINE: { bgColor: '#01C008', color: '#fff' },
	CLOSED: { bgColor: '#01C008', color: '#fff' },
	SUCCEEDED: { bgColor: '#01C008', color: '#fff' },
	IP_DELAYED_START: { bgColor: 'rgba(0, 96, 179, 0.2)', color: 'rgb(54, 168, 255, 1)' },
	'IN PROGRESS': { bgColor: '#ECB914', color: '#fff' },
	'LONG RUNNING': { bgColor: '#ECB914', color: '#fff' },
	'NOT MET': { bgColor: '#ECB914', color: '#fff' },
	'SLA MISSED': { bgColor: '#FE2626', color: '#fff' },
	'NOT STARTED': { bgColor: '#1B1E35CC', color: '#fff' },
	'N/A': { bgColor: '#71758F', color: '#fff' },
	NA: { bgColor: '#71758F', color: '#fff' },
	SCHEDULED: { bgColor: '#71758F', color: '#fff' },
	RUNNING: { bgColor: 'rgb(254 38 38 / 28%)', color: '#fff' },
	ACTIVATED: { bgColor: 'rgb(254 38 38 / 28%)', color: '#fff' },
	HOLD: { bgColor: 'rgb(254 38 38 / 28%)', color: '#fff' },
	FROZEN: { bgColor: 'rgb(254 38 38 / 28%)', color: '#fff' },
	'SLA MET': { bgColor: 'rgba(1, 192, 8, 0.2)', color: '#fff' },
	MET: { bgColor: 'rgba(1, 192, 8, 0.2)', color: '#fff' },
	HEALTHY: { bgColor: 'rgba(1, 192, 8, 0.2)', color: '#fff' },
	OFFLINE: { bgColor: 'rgb(253 120 59 / 0.7)', color: '#fff' },
	THROTTLED: { bgColor: 'rgb(253 120 59 / 0.7)', color: '#fff' },
	RISK: { bgColor: 'rgb(236 185 20 / 0.7)', color: '#fff' },
};

const DYNAMIC_STATUS_CONFIG: Record<string, { bg: string; fg: string }> = {
	DELAYEDSTART: { bg: 'rgba(255, 56, 176, 0.2)', fg: 'rgba(255, 56, 176, 1)' },
	DELAYED: { bg: 'rgba(255, 56, 176, 0.2)', fg: 'rgba(255, 56, 176, 1)' },
	'FAILED TO START': { bg: 'rgba(160, 129, 255, 0.2)', fg: 'rgba(160, 129, 255, 1)' },
	LONGRUNNING: { bg: 'rgba(253, 149, 50, 0.2)', fg: 'rgba(253, 149, 50, 1)' },
	JOBSFAILED: { bg: 'rgba(254, 38, 38, 0.15)', fg: 'rgba(254, 38, 38, 1)' },
	JOBSSUCCESS: { bg: 'rgba(1, 192, 8, 0.2)', fg: 'rgba(1, 192, 8, 1)' },
};

const SLA_BG_COLORS: Record<string, string> = {
	'SLA Met': '#01C008',
	'SLA Missed': '#FE2626',
	'Scheduled': '#71758F',
	'In Progress': '#ECB914',
};

function getSlaStatusColors(value: string | number | undefined) {
	if (value === '--') {
		return { bgColor: 'transparent', color: '#F5F5F5' };
	}
	const valStr = String(value);
	const bgColor = SLA_BG_COLORS[valStr] || '#2a4444';
	const color = valStr === 'Scheduled' ? 'rgb(241, 242, 244)' : 'rgba(255, 255, 255, 1)';
	return { bgColor, color };
}

function getFailureColors(columnName?: string) {
	return {
		bgColor: '#FE2626',
		color: '#fff',
	};
}

function getStatusColors(
	status: string,
	value?: number | string,
	columnName?: string,
	defaultBgColor?: string
): { bgColor: string; color: string } {
	const statusKey = status.toUpperCase();

	// 1. Check static styles map
	const staticStyle = STATIC_STATUS_STYLES[statusKey];
	if (staticStyle) {
		return { bgColor: staticStyle.bgColor, color: staticStyle.color };
	}

	// 2. Check dynamic simple config
	const dynamicConfig = DYNAMIC_STATUS_CONFIG[statusKey];
	if (dynamicConfig) {
		return {
			bgColor: value === '--' ? 'transparent' : dynamicConfig.bg,
			color: value === '--' ? '#F5F5F5' : dynamicConfig.fg,
		};
	}

	// 3. Handle specific custom dynamic cases
	let bgColor = defaultBgColor || 'transparent';
	let color = '#fff';

	switch (statusKey) {
		case 'OPEN':
			bgColor = defaultBgColor || 'transparent';
			color = '#299bff';
			break;

		case 'FAILURE':
		case 'TERMINATED':
		case 'SUSPENDED':
		case 'NO':
		case 'FAILED':
			return getFailureColors(columnName);

		case 'ACTUAL_COMPLETION_TIME':
			color = 'rgba(216, 43, 81, 1)';
			break;

		case 'NOT SCHEDULED':
			bgColor = value === '--' ? 'transparent' : '#2a4444';
			color = '#F5F5F5';
			break;

		case 'PROJECTSLASTATUS':
			return getSlaStatusColors(value);

		case '--':
			bgColor = 'transparent';
			break;

		default:
			bgColor = '#299BFF26';
			break;
	}

	return { bgColor, color };
}

const StatusChip: React.FC<{ status: string, value?: number | string, displayNumber?: boolean, columnName?: string } & ChipProps> = ({ status, value, displayNumber, columnName, ...props }) => {
	const theme = useTheme();
	const colors = theme.palette;
	const defaultBgColor = `${colors.backgroundLight.dark}`;
	
	const { bgColor, color } = getStatusColors(status || '--', value, columnName, defaultBgColor);

	const chipProps: ChipProps = {
		label: displayNumber ? value : status,
		sx: {
			borderRadius: status == 'ProjectSLAStatus' || columnName == 'totalExecutions' ? '50px' : '5px',
			'&.MuiChip-filled': {
				height: '30px',
				maxWidth: '100%',
				width: 'fit-content',
				minWidth: displayNumber ? '' : '150px'
			},
			'&.MuiChip-filled, & .MuiChip-label': {
				fontWeight: 500,
				margin: 0,
				fontFamily: 'Montserrat',
				overflow: "hidden",
				textTransform: 'uppercase'
			},
			'& .MuiChip-label': {
				paddingLeft: displayNumber ? '' : '8px',
				paddingRight: displayNumber ? '' : '8px'
			},
			// !important is given, becoz mui chip style is being overridden by other css files, remove !important once css files are removed
			bgcolor: `${bgColor} !important`,
			color: `${color} !important`
		},
		...props
	};

	return <Chip {...chipProps} />;
};

export default StatusChip;
